import React from 'react';
import styled from 'styled-components';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

type FilterSpec = {
  field: string;
  op: 'startsWith' | 'startsWithAny' | 'equals' | 'includes' | 'notIncludes' | 'in' | 'regex';
  value: string | string[];
};

type TabsMode = 'replace' | 'merge' | 'extend';

type TabConfig = {
  id?: string;
  label: string | { [key: string]: string | undefined };
  filterByGroup?: string[];
  filters?: FilterSpec[];
  columns?: string[];
  preferredColumnViewportSize?: number;
  forcedColumnViewportSize?: number;
  hidden?: boolean;
  order?: number;
  subTabsMode?: TabsMode;
  subTabs?: TabConfig[];
  defaultSubTab?: number;
};

type CellLabel = {
  type: 'label';
  value: string;
};

type CellOdd = {
  type: 'odd';
  outcome: any;
};

type CellOddWithName = {
  type: 'odd-w-name';
  outcome: any;
};

type CellEmpty = {
  type: 'empty';
};

type Cell = CellLabel | CellOdd | CellOddWithName | CellEmpty;

type HeaderCell = {
  label: string | Record<string, string>;
  value: string;
};

type TableRow = {
  cells: Cell[];
};

type MarketSection = {
  market: any;
  marketName: string;
  header: HeaderCell[];
  rows: TableRow[];
};

type PlayerPropGroupOption = {
  id: string;
  label: string;
  active: boolean;
  index: number;
  playerCount: number;
  variantCount: number;
};

type PlayerPropVariantOption = {
  id: string;
  label: string;
  active: boolean;
  index: number;
};

type PlayerPropLineOption = {
  value: string;
  label: string;
  active: boolean;
  index: number;
};

type PlayerPropRow = {
  playerName: string;
  outcome: any | null;
  hasOutcome: boolean;
};

type PlayerPropVariantData = {
  id: string;
  label: string;
  lines: string[];
  players: {
    playerName: string;
    outcomesByLine: Record<string, any>;
  }[];
};

type PlayerPropGroupData = {
  id: string;
  label: string;
  variants: PlayerPropVariantData[];
  playerCount: number;
};

type CorrectScoreColumnLabels = {
  home: string;
  draw: string;
  away: string;
};

type MarketMeta = {
  rawGroupId: string;
  tabGroup: string;
  hasArgument: boolean;
  columnKey: string;
  shapeKey: string;
  subTabKey: string;
};

type MarketFilterProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    markets?: any[];
    marketGroups?: any[];
    tabs?: TabConfig[];
    tabsMode?: TabsMode;
    hideAllGeneratedTabs?: boolean;
    lang?: string;
    sortBy?: string[];
  };
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const getFieldValue = (obj: any, fieldPath: string): any => {
  return fieldPath.split('.').reduce((o, k) => o?.[k], obj) ?? '';
};

const matchesFilter = (market: any, filter: FilterSpec): boolean => {
  const fieldValue = String(getFieldValue(market, filter.field));
  switch (filter.op) {
    case 'startsWith':
      return fieldValue.startsWith(String(filter.value));
    case 'startsWithAny':
      return Array.isArray(filter.value) && filter.value.some((v) => fieldValue.startsWith(v));
    case 'equals':
      return fieldValue === String(filter.value);
    case 'includes':
      return fieldValue.includes(String(filter.value));
    case 'notIncludes':
      return !fieldValue.includes(String(filter.value));
    case 'in':
      return Array.isArray(filter.value) && filter.value.includes(fieldValue);
    case 'regex':
      return new RegExp(String(filter.value)).test(fieldValue);
    default:
      return true;
  }
};

const matchesAllFilters = (market: any, filters: FilterSpec[]): boolean => {
  return filters.every((f) => matchesFilter(market, f));
};

const resolveLabel = (label: string | { [key: string]: string | undefined }, lang: string): string => {
  if (typeof label === 'string') return label;
  return label?.[lang] ?? label?.['2'] ?? label?.['0'] ?? '';
};

const DEFAULT_TAB_LABELS: Record<string, string> = {
  main: 'Main',
  '1st half': '1st Half',
  '2nd half': '2nd Half',
  corners: 'Corners',
  'yellow cards': 'Yellow Cards',
  'shots on target': 'Shots on Target',
  shots: 'Shots',
  fouls: 'Fouls',
  offsides: 'Offsides',
  players: 'Players',
  specials: 'Specials',
};

const DEFAULT_TAB_ORDER = [
  'main',
  '1st half',
  '2nd half',
  'corners',
  'yellow cards',
  'shots on target',
  'shots',
  'fouls',
  'offsides',
  'players',
  'specials',
];

type ShapeConfig = {
  label: string | Record<string, string>;
  columns?: string[];
  forcedColumnViewportSize?: number;
};

const SHAPE_CONFIGS: Record<string, ShapeConfig> = {
  'flat:no|yes': { label: 'Yes / No' },
  'arg:no|yes': { label: 'Yes / No by Line' },
  'flat:win1|win2|x': { label: '1X2', forcedColumnViewportSize: 3 },
  'flat:win1|win2|x::european-handicap': { label: 'European Handicap' },
  'flat:12|1x|x2': { label: 'Double Chance' },
  'flat:12|1x|draw|win1|win2': { label: 'Result by Time' },
  'flat:team 1|team 2': { label: 'Team 1 / Team 2' },
  'flat:no one|team 1|team 2': { label: 'No One / Team' },
  'arg:no one|team 1|team 2': { label: 'No One / Team by Line' },
  'arg:over|under': { label: 'Over / Under' },
  'arg:over|under::combo-totals': { label: 'Team + Total' },
  'arg:over|under::goal-minutes': { label: 'Minutes Markets' },
  'arg:over|under::minute-intervals': { label: 'Totals by Time' },
  'arg:equal|over|under': { label: '3 Way Totals' },
  'arg:handicap 1|handicap 2': { label: 'Handicap' },
  'arg:handicap 1|handicap 2::asian': { label: 'Asian Handicap' },
  'flat:draw|win1|win2': { label: 'Result 3 Way' },
  'flat:draw|win1|win2::half-or-match': { label: '1st Half or Match' },
  'flat:draw|win1|win2::timed-result': { label: 'Timed Result 3 Way' },
  'flat:12|1x|draw|win1|win2|x2': { label: 'Result + Double Chance' },
  'flat:in the 1st half|in the 2nd half|will not score': {
    label: 'Scoring Half',
  },
  'flat:1st < 2nd|1st = 2nd|1st > 2nd': { label: 'Highest Half' },
  'flat:even|odd': { label: 'Odd / Even' },
  'flat:draw and over|draw and under|win1 and over|win1 and under|win2 and over|win2 and under': {
    label: 'Result + Total',
  },
  'arg:draw and over|draw and under|win1 and over|win1 and under|win2 and over|win2 and under': {
    label: 'Result + Total',
  },
  'flat:draw or over|draw or under|win1 or over|win1 or under|win2 or over|win2 or under': {
    label: 'Result or Total',
  },
  'arg:draw or over|draw or under|win1 or over|win1 or under|win2 or over|win2 or under': {
    label: 'Result or Total',
  },
  'flat:draw and at least one team will not score|draw and both team to score|win1 and at least one team will not score|win1 and both teams to score|win2 and at least one team will not score|win2 and both teams to score':
    {
      label: 'Result + BTTS',
    },
  'flat:draw or at least one team will not score|draw or both teams to score|win1 or at least one team will not score|win1 or both teams to score|win2 or at least one team will not score|win2 or both teams to score':
    {
      label: 'Result or BTTS',
    },
  'arg:12 and over|12 and under|1x and over|1x and under|x2 and over|x2 and under': {
    label: 'Double Chance + Total',
  },
  'flat:12 and at least one team will not score|12 and both to score|1x and at least one team will not score|1x and both to score|x2 and at least one team will not score|x2 and both to score':
    {
      label: 'Double Chance + BTTS',
    },
  'arg:at least one team will not score and over|at least one team will not score and under|both teams to score and over|both teams to score and under':
    {
      label: 'Total + BTTS',
    },
  'arg:at least one team will not score or over|at least one team will not score or under|both teams to score or over|both teams to score or under':
    {
      label: 'Total or BTTS',
    },
  'flat:win1/win1|win1/win2|win1/x|win2/win1|win2/win2|win2/x|x/win1|x/win2|x/x': {
    label: 'HT / FT',
  },
  'flat:12/12|12/1x|12/x2|1x/12|1x/1x|1x/x2|x2/12|x2/1x|x2/x2': {
    label: 'HT / FT Double Chance',
  },
  'flat:to be carded|to be sent off|to receive a yellow card': {
    label: 'Player Cards',
    columns: ['to receive a yellow card', 'to be carded', 'to be sent off'],
  },
  'flat:to be carded|to be sent off|to receive a yellow card|to score|to score 2 or more|to score 3 or more': {
    label: 'Player Props',
    columns: ['to score', 'to score 2 or more', 'to score 3 or more'],
  },
};

const SHAPE_ORDER = [
  'flat:win1|win2|x',
  'flat:12|1x|x2',
  'flat:12|1x|draw|win1|win2',
  'flat:draw|win1|win2',
  'flat:draw|win1|win2::half-or-match',
  'flat:draw|win1|win2::timed-result',
  'flat:12|1x|draw|win1|win2|x2',
  'flat:team 1|team 2',
  'arg:over|under',
  'arg:over|under::combo-totals',
  'arg:over|under::goal-minutes',
  'arg:over|under::minute-intervals',
  'arg:equal|over|under',
  'arg:handicap 1|handicap 2',
  'arg:handicap 1|handicap 2::asian',
  'flat:win1|win2|x::european-handicap',
  'flat:even|odd',
  'flat:no|yes',
  'arg:no|yes',
  'flat:no one|team 1|team 2',
  'arg:no one|team 1|team 2',
  'flat:in the 1st half|in the 2nd half|will not score',
  'flat:1st < 2nd|1st = 2nd|1st > 2nd',
  'arg:draw and over|draw and under|win1 and over|win1 and under|win2 and over|win2 and under',
  'flat:draw and over|draw and under|win1 and over|win1 and under|win2 and over|win2 and under',
  'arg:draw or over|draw or under|win1 or over|win1 or under|win2 or over|win2 or under',
  'flat:draw or over|draw or under|win1 or over|win1 or under|win2 or over|win2 or under',
  'flat:draw and at least one team will not score|draw and both team to score|win1 and at least one team will not score|win1 and both teams to score|win2 and at least one team will not score|win2 and both teams to score',
  'flat:draw or at least one team will not score|draw or both teams to score|win1 or at least one team will not score|win1 or both teams to score|win2 or at least one team will not score|win2 or both teams to score',
  'arg:12 and over|12 and under|1x and over|1x and under|x2 and over|x2 and under',
  'flat:12 and at least one team will not score|12 and both to score|1x and at least one team will not score|1x and both to score|x2 and at least one team will not score|x2 and both to score',
  'arg:at least one team will not score and over|at least one team will not score and under|both teams to score and over|both teams to score and under',
  'arg:at least one team will not score or over|at least one team will not score or under|both teams to score or over|both teams to score or under',
  'flat:win1/win1|win1/win2|win1/x|win2/win1|win2/win2|win2/x|x/win1|x/win2|x/x',
  'flat:12/12|12/1x|12/x2|1x/12|1x/1x|1x/x2|x2/12|x2/1x|x2/x2',
  'flat:to be carded|to be sent off|to receive a yellow card|to score|to score 2 or more|to score 3 or more',
  'flat:to be carded|to be sent off|to receive a yellow card',
];

const SHAPE_ORDER_INDEX = SHAPE_ORDER.reduce((acc: Record<string, number>, curr, index) => {
  acc[curr] = index;
  return acc;
}, {});

const PLAYER_SUB_TAB_LABELS: Record<string, string> = {
  'players:scoring': 'Scoring',
  'players:shooting': 'Shooting',
  'players:assists': 'Assists',
  'players:passing': 'Passing',
  'players:rebounds': 'Rebounds',
  'players:defense': 'Defense',
  'players:discipline': 'Discipline',
  'players:combos': 'Combos',
  'players:specials': 'Specials',
  'players:other': 'Other',
};

const PLAYER_SUB_TAB_ORDER = [
  'players:scoring',
  'players:shooting',
  'players:assists',
  'players:passing',
  'players:rebounds',
  'players:defense',
  'players:discipline',
  'players:combos',
  'players:specials',
  'players:other',
];

const getPlayerColumnCategory = (columnKey: string): string => {
  if (!columnKey) return 'players:other';

  if (
    columnKey.includes(' and ') ||
    columnKey.includes('player to score or assist') ||
    columnKey.includes('steals and blocks') ||
    columnKey.includes('points, assists and rebounds')
  ) {
    return 'players:combos';
  }

  if (columnKey.includes('double double') || columnKey.includes('triple double')) {
    return 'players:specials';
  }

  if (
    columnKey.includes('yellow') ||
    columnKey.includes('carded') ||
    columnKey.includes('sent off') ||
    columnKey.includes('booked') ||
    columnKey.includes('fouls')
  ) {
    return 'players:discipline';
  }

  if (columnKey.includes('passes')) {
    return 'players:passing';
  }

  if (columnKey.includes('assists')) {
    return 'players:assists';
  }

  if (columnKey.includes('rebounds')) {
    return 'players:rebounds';
  }

  if (
    columnKey.includes('blocks') ||
    columnKey.includes('steals') ||
    columnKey.includes('tackles') ||
    columnKey.includes('saves') ||
    columnKey.includes('offsides')
  ) {
    return 'players:defense';
  }

  if (columnKey.includes('shots')) {
    return 'players:shooting';
  }

  if (
    columnKey.includes('score') ||
    columnKey.includes('goal') ||
    columnKey.includes('points') ||
    columnKey.includes('threes made')
  ) {
    return 'players:scoring';
  }

  return 'players:other';
};

const buildPlayerSubTabs = (markets: any[]): TabConfig[] => {
  const columnsByCategory = new Map<string, string[]>();
  const seenByCategory = new Map<string, Set<string>>();

  for (const market of markets) {
    for (const outcome of market.outcomes ?? []) {
      const columnKey = getOutcomeColumnKey(outcome);
      if (!columnKey) continue;

      const category = getPlayerColumnCategory(columnKey);
      if (!columnsByCategory.has(category)) {
        columnsByCategory.set(category, []);
        seenByCategory.set(category, new Set<string>());
      }

      const seen = seenByCategory.get(category);
      if (seen?.has(columnKey)) continue;

      seen?.add(columnKey);
      columnsByCategory.get(category)?.push(columnKey);
    }
  }

  return PLAYER_SUB_TAB_ORDER.filter((id) => (columnsByCategory.get(id)?.length ?? 0) > 0).map((id) => ({
    id,
    label: PLAYER_SUB_TAB_LABELS[id],
    filters: [{ field: '__meta.tabGroup', op: 'equals', value: 'players' }],
    columns: columnsByCategory.get(id),
  }));
};

const PLAYER_PROP_VARIANT_ORDER = ['base', 'equal or more', 'over', 'under', 'yes', 'no'];

const formatPlayerPropLabel = (value: string): string => {
  return value
    .split(/\s+/)
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ');
};

const parsePlayerPropColumn = (
  columnLabel: string,
): { groupId: string; groupLabel: string; variantId: string; variantLabel: string } => {
  const trimmed = String(columnLabel).trim();
  const match = trimmed.match(/^(.*?):\s*(equal or more|over|under|yes|no)$/i);

  if (!match) {
    const normalized = trimmed.toLowerCase();
    return {
      groupId: normalized,
      groupLabel: trimmed,
      variantId: 'base',
      variantLabel: 'Main',
    };
  }

  const groupLabel = match[1].trim();
  const variantLabel = match[2].trim();

  return {
    groupId: groupLabel.toLowerCase(),
    groupLabel,
    variantId: variantLabel.toLowerCase(),
    variantLabel: formatPlayerPropLabel(variantLabel),
  };
};

const compareNumericLikeStrings = (a: string, b: string): number => {
  const na = Number(a);
  const nb = Number(b);

  if (!Number.isNaN(na) && !Number.isNaN(nb)) {
    return na - nb;
  }

  return NATURAL_SORTER.compare(a, b);
};

const getPreferredPlayerPropVariantId = (variants: PlayerPropVariantData[]): string => {
  const ranked = [...variants].sort((a, b) => {
    const ai = PLAYER_PROP_VARIANT_ORDER.indexOf(a.id);
    const bi = PLAYER_PROP_VARIANT_ORDER.indexOf(b.id);

    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;

    return NATURAL_SORTER.compare(a.label, b.label);
  });

  return ranked[0]?.id ?? '';
};

const getPreferredPlayerPropLine = (lines: string[]): string => {
  if (lines.length === 0) return '';
  if (lines.length === 1) return lines[0];

  const sortedLines = [...lines].sort(compareNumericLikeStrings);
  return sortedLines[0];
};

const buildPlayerPropGroups = (markets: any[], lang: string): PlayerPropGroupData[] => {
  const playerMarkets = markets.filter((market: any) => market.__meta?.tabGroup === 'players');
  const groups = new Map<
    string,
    {
      id: string;
      label: string;
      variants: Map<
        string,
        {
          id: string;
          label: string;
          lines: Set<string>;
          players: Map<string, { playerName: string; outcomesByLine: Record<string, any> }>;
        }
      >;
      players: Set<string>;
    }
  >();

  for (const market of playerMarkets) {
    const playerName = getDisplayMarketName(market, lang);

    for (const outcome of market.outcomes ?? []) {
      const rawColumnLabel =
        resolveLabel(outcome.nameWithoutArgument ?? {}, lang) ||
        resolveLabel(outcome.name ?? {}, lang) ||
        outcome.oddName ||
        '';
      const parsed = parsePlayerPropColumn(rawColumnLabel);
      const lineKey = outcome.argument != null ? String(outcome.argument) : '__single';

      if (!groups.has(parsed.groupId)) {
        groups.set(parsed.groupId, {
          id: parsed.groupId,
          label: parsed.groupLabel,
          variants: new Map(),
          players: new Set<string>(),
        });
      }

      const group = groups.get(parsed.groupId)!;
      group.players.add(playerName);

      if (!group.variants.has(parsed.variantId)) {
        group.variants.set(parsed.variantId, {
          id: parsed.variantId,
          label: parsed.variantLabel,
          lines: new Set<string>(),
          players: new Map(),
        });
      }

      const variant = group.variants.get(parsed.variantId)!;
      variant.lines.add(lineKey);

      if (!variant.players.has(playerName)) {
        variant.players.set(playerName, {
          playerName,
          outcomesByLine: {},
        });
      }

      variant.players.get(playerName)!.outcomesByLine[lineKey] = outcome;
    }
  }

  return [...groups.values()]
    .map((group) => ({
      id: group.id,
      label: group.label,
      playerCount: group.players.size,
      variants: [...group.variants.values()]
        .map((variant) => ({
          id: variant.id,
          label: variant.label,
          lines: [...variant.lines].sort(compareNumericLikeStrings),
          players: [...variant.players.values()].sort((a, b) => NATURAL_SORTER.compare(a.playerName, b.playerName)),
        }))
        .sort((a, b) => {
          const ai = PLAYER_PROP_VARIANT_ORDER.indexOf(a.id);
          const bi = PLAYER_PROP_VARIANT_ORDER.indexOf(b.id);

          if (ai !== -1 && bi !== -1) return ai - bi;
          if (ai !== -1) return -1;
          if (bi !== -1) return 1;

          return NATURAL_SORTER.compare(a.label, b.label);
        }),
    }))
    .sort((a, b) => {
      if (a.playerCount !== b.playerCount) return b.playerCount - a.playerCount;
      return NATURAL_SORTER.compare(a.label, b.label);
    });
};

const getOutcomeColumnKey = (outcome: any): string => {
  const label = outcome.nameWithoutArgument?.['2'] ?? outcome.nameWithoutArgument?.['0'] ?? '';

  return String(label).trim().toLowerCase();
};

const getOutcomeColumnKeyForLang = (outcome: any, lang: string): string => {
  const label =
    outcome.nameWithoutArgument?.[lang] ??
    outcome.nameWithoutArgument?.['2'] ??
    outcome.nameWithoutArgument?.['0'] ??
    '';

  return String(label).trim().toLowerCase();
};

const getMarketColumns = (market: any): string[] => {
  const seen = new Set<string>();
  const columns: string[] = [];

  for (const outcome of market.outcomes ?? []) {
    const key = getOutcomeColumnKey(outcome);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    columns.push(key);
  }

  return columns.sort();
};

const getRawGroupId = (market: any): string => {
  return String(market.idMb ?? '').split('/')[0];
};

const normalizeGroupId = (groupId: string): string => {
  return groupId.startsWith('dup:') ? groupId.slice(4) : groupId;
};

const resolveTabGroupKey = (rawGroupId: string, marketGroups: any[]): string => {
  const normalizedGroupId = normalizeGroupId(rawGroupId);
  const matchedGroup = marketGroups.find((group) => group.id === rawGroupId || group.id === normalizedGroupId);
  const nameCompare = String(matchedGroup?.nameCompare ?? '')
    .trim()
    .toLowerCase();

  if (!nameCompare || nameCompare === 'all') return 'main';
  return nameCompare;
};

const getSubTabKey = (market: any, tabGroup: string, shapeKey: string): string => {
  const marketName = resolveLabel(market.name ?? {}, '2').toLowerCase();

  if (tabGroup === 'main') {
    if (shapeKey === 'arg:over|under') {
      if (
        marketName.includes('score first and total') ||
        marketName.includes('win1 and total team1') ||
        marketName.includes('win2 and total team2')
      ) {
        return `${shapeKey}::combo-totals`;
      }

      if (
        marketName.includes('goal minutes') ||
        marketName.includes('lead minutes') ||
        marketName.includes('minutes draw score')
      ) {
        return `${shapeKey}::goal-minutes`;
      }

      if (marketName.startsWith('total from ')) {
        return `${shapeKey}::minute-intervals`;
      }
    }

    if (shapeKey === 'flat:win1|win2|x' && marketName.includes('handicap')) {
      return `${shapeKey}::european-handicap`;
    }

    if (shapeKey === 'flat:draw|win1|win2') {
      if (marketName.includes('1st half or match')) {
        return `${shapeKey}::half-or-match`;
      }

      if (marketName.startsWith('result from ')) {
        return `${shapeKey}::timed-result`;
      }
    }

    if (shapeKey === 'arg:handicap 1|handicap 2' && marketName.includes('asian handicap')) {
      return `${shapeKey}::asian`;
    }
  }

  return shapeKey;
};

const buildMarketMeta = (market: any, marketGroups: any[]): MarketMeta => {
  const rawGroupId = getRawGroupId(market);
  const columns = getMarketColumns(market);
  const hasArgument = (market.outcomes ?? []).some((outcome: any) => outcome.argument != null);
  const columnKey = columns.join('|');
  const tabGroup = resolveTabGroupKey(rawGroupId, marketGroups);
  const shapeKey = `${hasArgument ? 'arg' : 'flat'}:${columnKey}`;

  return {
    rawGroupId,
    tabGroup,
    hasArgument,
    columnKey,
    shapeKey,
    subTabKey: getSubTabKey(market, tabGroup, shapeKey),
  };
};

const getTabLabel = (tabGroup: string, marketGroups: any[]): string | Record<string, string> => {
  if (DEFAULT_TAB_LABELS[tabGroup]) return DEFAULT_TAB_LABELS[tabGroup];

  const matchedGroup = marketGroups.find(
    (group) =>
      String(group.nameCompare ?? '')
        .trim()
        .toLowerCase() === tabGroup,
  );
  if (matchedGroup?.name) return matchedGroup.name;

  return tabGroup;
};

const getShapeVariantLabel = (marketsForShape: any[], lang: string): string | null => {
  const firstMarket = marketsForShape[0];
  const outcomeLabels = (firstMarket?.outcomes ?? [])
    .map((outcome: any) => resolveLabel(outcome.name ?? outcome.nameWithoutArgument ?? {}, lang))
    .filter(Boolean);

  if (outcomeLabels.length === 0) return null;

  const intervalMatches = outcomeLabels
    .map((label: string) => {
      const match = label.match(/from\s+(\d+)\s+to\s+(\d+)/i);
      if (!match) return null;
      const start = Number(match[1]);
      const end = Number(match[2]);
      if (Number.isNaN(start) || Number.isNaN(end)) return null;
      return { start, end, span: end - start + 1 };
    })
    .filter(
      (
        value: { start: number; end: number; span: number } | null,
      ): value is { start: number; end: number; span: number } => value != null,
    );

  if (intervalMatches.length === outcomeLabels.length) {
    const spans = [...new Set(intervalMatches.map((item: { start: number; end: number; span: number }) => item.span))];
    const startsAtOneCount = intervalMatches.filter(
      (item: { start: number; end: number; span: number }) => item.start === 1,
    ).length;
    const endsAtNinetyCount = intervalMatches.filter(
      (item: { start: number; end: number; span: number }) => item.end === 90,
    ).length;

    if (startsAtOneCount > 0 && endsAtNinetyCount > 0 && spans.length > 1) {
      return 'cutoff';
    }

    if (spans.length === 1) {
      return `${spans[0]}m`;
    }

    return 'custom';
  }

  return null;
};

const getShapeConfig = (shapeKey: string, marketsForShape: any[], lang: string): ShapeConfig => {
  if (SHAPE_CONFIGS[shapeKey]) return SHAPE_CONFIGS[shapeKey];

  const names = [...new Set(marketsForShape.map((market) => resolveLabel(market.name ?? {}, lang)).filter(Boolean))];
  if (names.length > 0) {
    const variantLabel = names.length === 1 ? getShapeVariantLabel(marketsForShape, lang) : null;
    return { label: variantLabel ? `${names[0]} (${variantLabel})` : names[0] };
  }

  return { label: shapeKey.replace(/^(flat|arg):/, '') };
};

const compareTabOrder = (a: TabConfig, b: TabConfig): number => {
  const ai = a.order;
  const bi = b.order;

  if (ai != null && bi != null) return ai - bi;
  if (ai != null) return -1;
  if (bi != null) return 1;
  return 0;
};

const finalizeTabConfigs = (tabs: TabConfig[]): TabConfig[] => {
  return [...tabs]
    .filter((tab) => !tab.hidden)
    .map((tab) => ({
      ...tab,
      subTabs: tab.subTabs ? finalizeTabConfigs(tab.subTabs) : undefined,
    }))
    .sort(compareTabOrder);
};

const mergeTabConfigLists = (baseTabs: TabConfig[], customTabs: TabConfig[]): TabConfig[] => {
  const result: TabConfig[] = [];
  const customById = new Map<string, TabConfig>();
  const customWithoutId: TabConfig[] = [];

  for (const customTab of customTabs) {
    if (customTab.id) {
      customById.set(customTab.id, customTab);
    } else {
      customWithoutId.push(customTab);
    }
  }

  for (const baseTab of baseTabs) {
    const customTab = baseTab.id ? customById.get(baseTab.id) : undefined;
    if (!customTab) {
      result.push(baseTab);
      continue;
    }

    if (baseTab.id) {
      customById.delete(baseTab.id);
    }

    if (customTab.hidden) {
      continue;
    }

    const nextSubTabs =
      customTab.subTabs == null
        ? baseTab.subTabs
        : customTab.subTabsMode === 'replace' || !baseTab.subTabs
          ? finalizeTabConfigs(customTab.subTabs)
          : mergeTabConfigLists(baseTab.subTabs, customTab.subTabs);

    result.push({
      ...baseTab,
      ...customTab,
      subTabs: nextSubTabs,
    });
  }

  result.push(...[...customById.values(), ...customWithoutId].filter((tab) => !tab.hidden));

  return finalizeTabConfigs(result);
};

const applyTabsConfig = (
  generatedTabs: TabConfig[],
  configuredTabs?: TabConfig[],
  tabsMode: TabsMode = 'replace',
  hideAllGeneratedTabs = false,
): TabConfig[] => {
  const baseGeneratedTabs = hideAllGeneratedTabs
    ? generatedTabs.map((tab) => ({
        ...tab,
        hidden: true,
      }))
    : generatedTabs;

  if (!configuredTabs || configuredTabs.length === 0) {
    return finalizeTabConfigs(baseGeneratedTabs);
  }

  if (tabsMode === 'replace') {
    return finalizeTabConfigs(configuredTabs);
  }

  return mergeTabConfigLists(baseGeneratedTabs, configuredTabs);
};

const buildDefaultTabs = (markets: any[], marketGroups: any[], lang: string): TabConfig[] => {
  const tabGroups = new Map<string, any[]>();

  for (const market of markets) {
    const tabGroup = market.__meta?.tabGroup ?? 'main';
    if (!tabGroups.has(tabGroup)) {
      tabGroups.set(tabGroup, []);
    }
    tabGroups.get(tabGroup)?.push(market);
  }

  return [...tabGroups.entries()]
    .sort(([a], [b]) => {
      const ai = DEFAULT_TAB_ORDER.indexOf(a);
      const bi = DEFAULT_TAB_ORDER.indexOf(b);

      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;

      return resolveLabel(getTabLabel(a, marketGroups), lang).localeCompare(
        resolveLabel(getTabLabel(b, marketGroups), lang),
      );
    })
    .map(([tabGroup, tabMarkets]) => {
      if (tabGroup === 'players') {
        return {
          id: tabGroup,
          label: getTabLabel(tabGroup, marketGroups),
          filters: [{ field: '__meta.tabGroup', op: 'equals', value: tabGroup }],
          defaultSubTab: 0,
          subTabs: buildPlayerSubTabs(tabMarkets),
        };
      }

      const shapes = new Map<string, any[]>();

      for (const market of tabMarkets) {
        const shapeKey = market.__meta?.subTabKey ?? market.__meta?.shapeKey;
        if (!shapeKey) continue;
        if (!shapes.has(shapeKey)) {
          shapes.set(shapeKey, []);
        }
        shapes.get(shapeKey)?.push(market);
      }

      const subTabs = [...shapes.entries()]
        .sort(([shapeA, marketsA], [shapeB, marketsB]) => {
          const ai = SHAPE_ORDER_INDEX[shapeA];
          const bi = SHAPE_ORDER_INDEX[shapeB];
          const labelA = resolveLabel(getShapeConfig(shapeA, marketsA, lang).label, lang);
          const labelB = resolveLabel(getShapeConfig(shapeB, marketsB, lang).label, lang);

          if (ai != null && bi != null) return ai - bi;
          if (ai != null) return -1;
          if (bi != null) return 1;

          const labelCompare = labelA.localeCompare(labelB);
          if (labelCompare !== 0) return labelCompare;

          if (marketsA.length !== marketsB.length) return marketsB.length - marketsA.length;

          return 0;
        })
        .map(([shapeKey, shapeMarkets]) => {
          const shapeConfig = getShapeConfig(shapeKey, shapeMarkets, lang);

          return {
            id: shapeKey,
            label: shapeConfig.label,
            filters: [{ field: '__meta.subTabKey', op: 'equals', value: shapeKey }] as FilterSpec[],
            columns: shapeConfig.columns,
            forcedColumnViewportSize: shapeConfig.forcedColumnViewportSize,
          };
        });

      return {
        id: tabGroup,
        label: getTabLabel(tabGroup, marketGroups),
        filters: [{ field: '__meta.tabGroup', op: 'equals', value: tabGroup }],
        defaultSubTab: 0,
        subTabs,
      };
    });
};

const isFlat = (section: MarketSection): boolean => {
  // Flat sections use an empty first header cell; argument-based sections put the market name there.
  return section.header[0]?.label === '' && section.rows.length === 1 && section.rows[0].cells[0]?.type === 'label';
};

// check if two headers are "compatible" — they share at least one non-_label column
const headersOverlap = (a: HeaderCell[], b: HeaderCell[]): boolean => {
  const aValues = new Set(a.filter((h) => h.value !== '_label').map((h) => h.value));
  return b.some((h) => h.value !== '_label' && aValues.has(h.value));
};

const shouldTransposeFlatGroup = (group: MarketSection[], unionColumns: HeaderCell[]): boolean => {
  return unionColumns.length >= 5 && unionColumns.length > group.length;
};

const buildTransposedFlatSection = (
  group: MarketSection[],
  unionColumns: HeaderCell[],
  lang: string,
): MarketSection => {
  const sortedGroup = [...group].sort((a, b) => NATURAL_SORTER.compare(a.marketName, b.marketName));
  const sortedUnionColumns = [...unionColumns].sort((a, b) =>
    NATURAL_SORTER.compare(resolveLabel(a.label, lang), resolveLabel(b.label, lang)),
  );

  const marketHeaders: HeaderCell[] = sortedGroup.map((section, index) => ({
    label: section.marketName,
    value: `market:${section.market?.idMb ?? section.marketName ?? index}`,
  }));

  const rows: TableRow[] = sortedUnionColumns.map((column) => {
    const labelCell: CellLabel = {
      type: 'label',
      value: resolveLabel(column.label, lang),
    };

    const oddCells: Cell[] = sortedGroup.map((section) => {
      const sourceIndex = section.header.findIndex((header) => header.value === column.value);
      return sourceIndex > 0
        ? section.rows[0]?.cells[sourceIndex] ?? ({ type: 'empty' } as CellEmpty)
        : ({ type: 'empty' } as CellEmpty);
    });

    return { cells: [labelCell, ...oddCells] };
  });

  const labelHeader: HeaderCell =
    sortedGroup.length === 1
      ? { label: sortedGroup[0]?.marketName ?? '', value: '_label' }
      : { label: '', value: '_label' };

  const resolvedMarketHeaders =
    sortedGroup.length === 1 ? marketHeaders.map((header) => ({ ...header, label: '' })) : marketHeaders;

  return {
    market: null,
    marketName: '',
    header: [labelHeader, ...resolvedMarketHeaders],
    rows: rows.filter(rowHasOdds),
  };
};

const mergeFlatSections = (sections: MarketSection[], lang: string): MarketSection[] => {
  if (sections.length === 0) return sections;

  const merged: MarketSection[] = [];
  let i = 0;

  while (i < sections.length) {
    const current = sections[i];

    if (!isFlat(current)) {
      merged.push(current);
      i++;
      continue;
    }

    // collect consecutive flat sections with overlapping headers
    const group: MarketSection[] = [current];
    let j = i + 1;
    while (j < sections.length && isFlat(sections[j]) && headersOverlap(current.header, sections[j].header)) {
      group.push(sections[j]);
      j++;
    }

    if (group.length > 1) {
      // build union header preserving order from first section, appending new columns from others
      const unionColumns: HeaderCell[] = [];
      const seen = new Set<string>();

      for (const section of group) {
        for (const h of section.header) {
          if (h.value !== '_label' && !seen.has(h.value)) {
            seen.add(h.value);
            unionColumns.push(h);
          }
        }
      }

      if (shouldTransposeFlatGroup(group, unionColumns)) {
        merged.push(buildTransposedFlatSection(group, unionColumns, lang));
      } else {
        const unionHeader: HeaderCell[] = [{ label: '', value: '_label' }, ...unionColumns];

        // remap each section's row to the union column order, filling empties
        const mergedRows: TableRow[] = [];
        for (const section of group) {
          const sectionColMap = new Map<string, Cell>();
          const sectionHeader = section.header.filter((h) => h.value !== '_label');
          const sectionCells = section.rows[0].cells;

          // first cell is the label, rest map to the section's own header
          const labelCell = sectionCells[0];
          sectionHeader.forEach((h, idx) => {
            sectionColMap.set(h.value, sectionCells[idx + 1]);
          });

          const remappedCells: Cell[] = [labelCell];
          for (const col of unionColumns) {
            remappedCells.push(sectionColMap.get(col.value) ?? ({ type: 'empty' } as CellEmpty));
          }

          mergedRows.push({ cells: remappedCells });
        }

        merged.push({
          market: null,
          marketName: '',
          header: unionHeader,
          rows: mergedRows,
        });
      }
    } else {
      const singleColumns = current.header.filter((header) => header.value !== '_label');
      if (shouldTransposeFlatGroup([current], singleColumns)) {
        merged.push(buildTransposedFlatSection([current], singleColumns, lang));
      } else {
        merged.push(current);
      }
    }

    i = j;
  }

  return merged;
};

const buildSortIndex = (sortBy: string[]): Record<string, number> => {
  return sortBy.reduce((acc: Record<string, number>, curr, index) => {
    acc[curr] = index;
    return acc;
  }, {});
};

const formatColumnFallbackLabel = (value: string): string => {
  return value.replace(/\b([a-z])/g, (match) => match.toUpperCase());
};

const NATURAL_SORTER = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const MIN_TABLE_LABEL_COLUMN_WIDTH = 84;
const MAX_TABLE_LABEL_COLUMN_WIDTH = 180;
const MIN_TABLE_ODD_COLUMN_WIDTH = 60;
const MAX_TABLE_ODD_COLUMN_WIDTH = 168;
const TABLE_LABEL_CHAR_WIDTH = 7.2;
const TABLE_ODD_HEADER_CHAR_WIDTH = 6.4;
const TABLE_COLUMN_EXTRA_PADDING = 28;
const TABLE_HORIZONTAL_PADDING = 24;

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

const estimateTextWidth = (text: string, minWidth: number, maxWidth: number, charWidth: number): number => {
  return clamp(Math.round(String(text).trim().length * charWidth + TABLE_COLUMN_EXTRA_PADDING), minWidth, maxWidth);
};

const getDefaultColumnViewportSize = (
  moduleWidth: number,
  labelColumnWidth: number,
  columnWidths: number[],
  preferredColumnViewportSize?: number,
): number => {
  const totalColumnCount = columnWidths.length;
  if (totalColumnCount <= 0) return 0;

  const maxRequestedColumns =
    preferredColumnViewportSize != null && preferredColumnViewportSize > 0
      ? Math.min(preferredColumnViewportSize, totalColumnCount)
      : totalColumnCount;

  if (moduleWidth <= 0) {
    return Math.min(maxRequestedColumns, totalColumnCount <= 3 ? totalColumnCount : 2);
  }

  const availableOddsWidth = Math.max(
    MIN_TABLE_ODD_COLUMN_WIDTH,
    moduleWidth - TABLE_HORIZONTAL_PADDING - labelColumnWidth,
  );

  let fittedColumnCount = 0;
  let usedWidth = 0;

  for (const width of columnWidths.slice(0, maxRequestedColumns)) {
    if (fittedColumnCount > 0 && usedWidth + width > availableOddsWidth) {
      break;
    }

    if (fittedColumnCount === 0 && width > availableOddsWidth) {
      fittedColumnCount = 1;
      break;
    }

    usedWidth += width;
    fittedColumnCount += 1;
  }

  return Math.max(1, fittedColumnCount);
};

const getEstimatedFullTableWidth = (labelColumnWidth: number, columnWidths: number[]): number => {
  if (columnWidths.length === 0) return 0;
  return labelColumnWidth + columnWidths.reduce((sum, width) => sum + width, 0) + TABLE_HORIZONTAL_PADDING;
};

const escapeRegExp = (value: string): string => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const getDisplayMarketName = (market: any, lang: string): string => {
  const marketName = resolveLabel(market.name ?? {}, lang);
  const tabGroup = market.__meta?.tabGroup;

  if (tabGroup && tabGroup !== 'main') {
    const prefixPattern = new RegExp(`^${escapeRegExp(String(tabGroup))}\\s*-\\s*`, 'i');
    return marketName.replace(prefixPattern, '');
  }

  return marketName;
};

const SCORELINE_PATTERN = /^(\d+)\s*:\s*(\d+)$/;

const parseScorelineOutcome = (outcome: any, lang: string): { home: number; away: number; rawLabel: string } | null => {
  const rawLabel = String(
    resolveLabel(outcome.nameWithoutArgument ?? outcome.name ?? {}, lang) || outcome.oddName || '',
  ).trim();
  const match = rawLabel.match(SCORELINE_PATTERN);

  if (!match) return null;

  return {
    home: Number(match[1]),
    away: Number(match[2]),
    rawLabel,
  };
};

const compareCorrectScoreOutcomes = (a: any, b: any, side: 'home' | 'draw' | 'away', lang: string): number => {
  const parsedA = parseScorelineOutcome(a, lang);
  const parsedB = parseScorelineOutcome(b, lang);

  if (!parsedA || !parsedB) {
    return NATURAL_SORTER.compare(String(a?.oddName ?? ''), String(b?.oddName ?? ''));
  }

  if (side === 'draw') {
    return parsedA.home - parsedB.home;
  }

  if (side === 'home') {
    if (parsedA.home !== parsedB.home) return parsedA.home - parsedB.home;
    return parsedA.away - parsedB.away;
  }

  if (parsedA.away !== parsedB.away) return parsedA.away - parsedB.away;
  return parsedA.home - parsedB.home;
};

const isCorrectScoreMarket = (market: any, lang: string): boolean => {
  const marketName = resolveLabel(market.name ?? {}, lang).toLowerCase();
  const outcomes = market.outcomes ?? [];

  if (!marketName.includes('correct score') || outcomes.length === 0) {
    return false;
  }

  return outcomes.every((outcome: any) => outcome.argument == null && parseScorelineOutcome(outcome, lang) != null);
};

const buildCorrectScoreSection = (
  market: any,
  marketName: string,
  lang: string,
  columnLabels: CorrectScoreColumnLabels,
): MarketSection => {
  const outcomesBySide: Record<'home' | 'draw' | 'away', CellOddWithName[]> = {
    home: [],
    draw: [],
    away: [],
  };

  for (const outcome of market.outcomes ?? []) {
    const parsed = parseScorelineOutcome(outcome, lang);
    if (!parsed) continue;

    const side: 'home' | 'draw' | 'away' =
      parsed.home > parsed.away ? 'home' : parsed.home === parsed.away ? 'draw' : 'away';

    outcomesBySide[side].push({
      type: 'odd-w-name',
      outcome,
    } as CellOddWithName);
  }

  (['home', 'draw', 'away'] as const).forEach((side) => {
    outcomesBySide[side].sort((a, b) => compareCorrectScoreOutcomes(a.outcome, b.outcome, side, lang));
  });

  const maxLength = Math.max(outcomesBySide.home.length, outcomesBySide.draw.length, outcomesBySide.away.length);
  const rows: TableRow[] = Array.from({ length: maxLength }, (_, index) => ({
    cells: [
      outcomesBySide.home[index] ?? ({ type: 'empty' } as CellEmpty),
      outcomesBySide.draw[index] ?? ({ type: 'empty' } as CellEmpty),
      outcomesBySide.away[index] ?? ({ type: 'empty' } as CellEmpty),
    ],
  })).filter(rowHasOdds);

  return {
    market,
    marketName,
    header: [
      { label: columnLabels.home, value: 'correct-score:home' },
      { label: columnLabels.draw, value: 'correct-score:draw' },
      { label: columnLabels.away, value: 'correct-score:away' },
    ],
    rows,
  };
};

const rowHasOdds = (row: TableRow): boolean => {
  return row.cells.some((cell) => cell.type === 'odd' || cell.type === 'odd-w-name');
};

const buildVisibleSections = (sections: MarketSection[], visibleColumns: HeaderCell[]): MarketSection[] => {
  if (visibleColumns.length === 0) {
    return sections.map((section) => ({
      ...section,
      header: section.header[0]?.value === '_label' && section.header.length > 0 ? [section.header[0]] : [],
      rows: section.rows.map((row) => ({
        cells: section.header[0]?.value === '_label' && row.cells.length > 0 ? [row.cells[0]] : [],
      })),
    }));
  }

  return sections.map((section) => {
    const hasLabelColumn = section.header[0]?.value === '_label';
    const labelHeader = hasLabelColumn ? section.header[0] ?? { label: '', value: '_label' } : null;
    const sectionColumnIndex = new Map<string, number>();

    section.header
      .filter((header) => header.value !== '_label')
      .forEach((header, index) => {
        sectionColumnIndex.set(header.value, hasLabelColumn ? index + 1 : index);
      });

    return {
      ...section,
      header: [
        ...(labelHeader ? [labelHeader] : []),
        ...visibleColumns.map((column) => section.header.find((header) => header.value === column.value) ?? column),
      ],
      rows: section.rows.map((row) => ({
        cells: [
          ...(hasLabelColumn ? [row.cells[0] ?? ({ type: 'empty' } as CellEmpty)] : []),
          ...visibleColumns.map((column) => {
            const sourceIndex = sectionColumnIndex.get(column.value);
            return sourceIndex != null
              ? row.cells[sourceIndex] ?? ({ type: 'empty' } as CellEmpty)
              : ({ type: 'empty' } as CellEmpty);
          }),
        ],
      })),
    };
  });
};

const buildMarketSection = (
  market: any,
  lang: string,
  sortIndex: Record<string, number>,
  allowedColumns?: Set<string>,
  correctScoreColumnLabels: CorrectScoreColumnLabels = {
    home: 'Home',
    draw: 'Draw',
    away: 'Away',
  },
): MarketSection => {
  const allOutcomes: any[] = market.outcomes ?? [];
  const outcomes = allowedColumns
    ? allOutcomes.filter((o: any) => allowedColumns.has(getOutcomeColumnKey(o)))
    : allOutcomes;
  const marketName = getDisplayMarketName(market, lang);

  if (isCorrectScoreMarket(market, lang)) {
    return buildCorrectScoreSection(market, marketName, lang, correctScoreColumnLabels);
  }

  const hasArgument = outcomes.some((o: any) => o.argument != null);

  // extract unique column names from nameWithoutArgument
  const columnsMap: Record<string, HeaderCell> = {};
  const detectedColumnOrder: string[] = [];
  outcomes.forEach((o: any) => {
    const colKey = getOutcomeColumnKeyForLang(o, lang);
    if (colKey && !columnsMap[colKey]) {
      columnsMap[colKey] = {
        label: o.nameWithoutArgument ?? formatColumnFallbackLabel(colKey),
        value: colKey,
      };
      detectedColumnOrder.push(colKey);
    }
  });

  const columnOrder = allowedColumns
    ? [...allowedColumns].filter((colKey) => columnsMap[colKey] != null || allowedColumns.has(colKey))
    : detectedColumnOrder;

  for (const colKey of columnOrder) {
    if (!columnsMap[colKey]) {
      columnsMap[colKey] = {
        label: formatColumnFallbackLabel(colKey),
        value: colKey,
      };
    }
  }

  // sort columns by sortBy config when we're not already using an explicit allowedColumns order
  if (!allowedColumns) {
    columnOrder.sort((a, b) => {
      const ai = sortIndex[a];
      const bi = sortIndex[b];
      if (ai != null && bi != null) return ai - bi;
      if (ai != null) return -1;
      if (bi != null) return 1;
      return 0;
    });
  }

  const header: HeaderCell[] = columnOrder.map((key) => columnsMap[key]);

  if (header.length === 0) {
    return {
      market,
      marketName,
      header: [],
      rows: [],
    };
  }

  if (outcomes.length === 0) {
    return {
      market,
      marketName,
      header: [],
      rows: [],
    };
  }

  if (hasArgument) {
    // grouped by argument: argument in first column, market name as section header
    const groupedHeader: HeaderCell[] = [{ label: marketName, value: '_label' }, ...header];

    const argGroups: Record<string, any[]> = {};
    const argOrder: string[] = [];

    outcomes.forEach((o: any) => {
      if (o.argument == null) return;
      const argKey = String(o.argument);
      if (!argGroups[argKey]) {
        argGroups[argKey] = [];
        argOrder.push(argKey);
      }
      argGroups[argKey].push(o);
    });

    argOrder.sort((a, b) => parseFloat(a) - parseFloat(b));

    const rows: TableRow[] = argOrder
      .map((argKey) => {
        const argOutcomes = argGroups[argKey];
        const labelCell: CellLabel = { type: 'label', value: argKey };

        const oddCells: Cell[] = columnOrder.map((colKey) => {
          const outcome = argOutcomes.find((o: any) => {
            const oKey = getOutcomeColumnKeyForLang(o, lang);
            return oKey === colKey;
          });
          if (outcome) {
            return { type: 'odd', outcome } as CellOdd;
          }
          return { type: 'empty' } as CellEmpty;
        });

        return { cells: [labelCell, ...oddCells] };
      })
      .filter(rowHasOdds);

    if (rows.length === 0) {
      return {
        market,
        marketName,
        header: [],
        rows: [],
      };
    }

    return { market, marketName, header: groupedHeader, rows };
  } else {
    // flat: single row, market name as first label cell
    const flatHeader: HeaderCell[] = [{ label: '', value: '_label' }, ...header];
    const nameCell: CellLabel = { type: 'label', value: marketName };
    const row: TableRow = {
      cells: [
        nameCell,
        ...columnOrder.map((colKey) => {
          const outcome = outcomes.find((o: any) => {
            const oKey = getOutcomeColumnKeyForLang(o, lang);
            return oKey === colKey;
          });
          if (outcome) {
            return { type: 'odd', outcome } as CellOdd;
          }
          return { type: 'empty' } as CellEmpty;
        }),
      ],
    };

    if (!rowHasOdds(row)) {
      return {
        market,
        marketName,
        header: [],
        rows: [],
      };
    }

    return { market, marketName, header: flatHeader, rows: [row] };
  }
};

const MarketFilter = (componentProps: MarketFilterProps) => {
  let props = componentProps;
  let isVisible = true;
  const moduleRef = React.useRef<HTMLDivElement | null>(null);

  const dataElementContext = React.useContext(DataElementContext);
  [props, isVisible] = processComponentProps(props, dataElementContext);

  const {
    markets = [],
    marketGroups = [],
    lang = '2',
    sortBy = [],
    tabsMode = 'replace',
    hideAllGeneratedTabs = false,
  } = props.properties ?? {};
  const configuredTabs = props.properties?.tabs;
  const correctScoreColumnLabels = React.useMemo<CorrectScoreColumnLabels>(
    () => ({
      home: dataElementContext?.parentContext?.match?.team1Name ?? 'Home',
      draw: 'Draw',
      away: dataElementContext?.parentContext?.match?.team2Name ?? 'Away',
    }),
    [dataElementContext],
  );

  const preparedMarkets = React.useMemo(() => {
    return markets.map((market: any) => ({
      ...market,
      __meta: buildMarketMeta(market, marketGroups),
    }));
  }, [markets, marketGroups]);

  const [activeTab, setActiveTab] = React.useState(0);

  const tabs = React.useMemo((): TabConfig[] => {
    const generatedTabs = buildDefaultTabs(preparedMarkets, marketGroups, lang);
    return applyTabsConfig(generatedTabs, configuredTabs, tabsMode, hideAllGeneratedTabs);
  }, [configuredTabs, preparedMarkets, marketGroups, lang, tabsMode, hideAllGeneratedTabs]);

  const [activeSubTab, setActiveSubTab] = React.useState<number>(() => {
    return tabs[0]?.defaultSubTab ?? 0;
  });
  const [activePlayerPropGroupId, setActivePlayerPropGroupId] = React.useState('');
  const [activePlayerPropVariantId, setActivePlayerPropVariantId] = React.useState('');
  const [activePlayerPropLine, setActivePlayerPropLine] = React.useState('');
  const [moduleWidth, setModuleWidth] = React.useState(0);

  const groupIdByName = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const g of marketGroups) {
      if (g.nameCompare) {
        map[g.nameCompare] = g.id;
      }
    }
    return map;
  }, [marketGroups]);

  const resolveGroupFilters = React.useCallback(
    (groupNames?: string[]): FilterSpec[] => {
      if (!groupNames || groupNames.length === 0) return [];
      if (groupNames.includes('all')) return [];

      const prefixes = groupNames
        .map((name) => groupIdByName[name])
        .filter(Boolean)
        .map((id) => `${id}/`);

      if (prefixes.length === 0) return [];
      if (prefixes.length === 1) {
        return [{ field: 'idMb', op: 'startsWith', value: prefixes[0] }];
      }
      return [{ field: 'idMb', op: 'startsWithAny', value: prefixes }];
    },
    [groupIdByName],
  );

  const [columnViewportStart, setColumnViewportStart] = React.useState(0);
  const [columnViewportSize, setColumnViewportSize] = React.useState<number | null>(null);

  const onTabClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const idx = Number(e.currentTarget.dataset.tabIndex);
      if (isNaN(idx)) return;

      try {
        e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      } catch (e) {
        /** */
      }

      setActiveTab(idx);
      setActiveSubTab(tabs[idx]?.defaultSubTab ?? 0);
      setActivePlayerPropGroupId('');
      setActivePlayerPropVariantId('');
      setActivePlayerPropLine('');
      setColumnViewportStart(0);
      setColumnViewportSize(null);
      e.stopPropagation();
    },
    [tabs],
  );

  const onSubTabClick = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const idx = Number(e.currentTarget.dataset.subtabIndex);
    if (isNaN(idx)) return;

    try {
      e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    } catch (e) {
      /** */
    }

    setActiveSubTab(idx);
    setActivePlayerPropGroupId('');
    setActivePlayerPropVariantId('');
    setActivePlayerPropLine('');
    setColumnViewportStart(0);
    setColumnViewportSize(null);
    e.stopPropagation();
  }, []);

  const currentTab = tabs[activeTab];
  const currentSubTabs = React.useMemo(() => currentTab?.subTabs ?? [], [currentTab]);
  const currentSubTab = currentSubTabs[activeSubTab];

  React.useEffect(() => {
    const element = moduleRef.current;
    if (!element) return;

    const updateWidth = (width: number) => {
      setModuleWidth((prev) => (prev === width ? prev : width));
    };

    updateWidth(element.getBoundingClientRect().width);

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        updateWidth(Math.round(entry.contentRect.width));
      });

      observer.observe(element);
      return () => observer.disconnect();
    }

    const onResize = () => {
      updateWidth(Math.round(element.getBoundingClientRect().width));
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  React.useEffect(() => {
    if (activeTab > tabs.length - 1) {
      setActiveTab(0);
      setActiveSubTab(tabs[0]?.defaultSubTab ?? 0);
      return;
    }

    if (activeSubTab > currentSubTabs.length - 1) {
      setActiveSubTab(currentTab?.defaultSubTab ?? 0);
    }
  }, [tabs, currentTab, currentSubTabs, activeTab, activeSubTab]);

  const filteredMarkets = React.useMemo(() => {
    if (!currentTab) return preparedMarkets;

    // Sub-tab's filterByGroup overrides parent's when present
    const groupFilters = resolveGroupFilters(currentSubTab?.filterByGroup ?? currentTab.filterByGroup);
    const parentFilters = currentTab.filters ?? [];
    const subFilters = currentSubTab?.filters ?? [];

    const allFilters = [...groupFilters, ...parentFilters, ...subFilters];
    return preparedMarkets.filter((m: any) => matchesAllFilters(m, allFilters));
  }, [preparedMarkets, currentTab, currentSubTab, resolveGroupFilters]);

  const playerPropGroupsData = React.useMemo(
    () => buildPlayerPropGroups(filteredMarkets, lang),
    [filteredMarkets, lang],
  );
  const activePlayerPropGroup = React.useMemo(() => {
    return (
      playerPropGroupsData.find((group) => group.id === activePlayerPropGroupId) ?? playerPropGroupsData[0] ?? null
    );
  }, [playerPropGroupsData, activePlayerPropGroupId]);
  const activePlayerPropVariant = React.useMemo(() => {
    if (!activePlayerPropGroup) return null;

    return (
      activePlayerPropGroup.variants.find((variant) => variant.id === activePlayerPropVariantId) ??
      activePlayerPropGroup.variants.find(
        (variant) => variant.id === getPreferredPlayerPropVariantId(activePlayerPropGroup.variants),
      ) ??
      activePlayerPropGroup.variants[0] ??
      null
    );
  }, [activePlayerPropGroup, activePlayerPropVariantId]);
  const activePlayerPropResolvedLine = React.useMemo(() => {
    if (!activePlayerPropVariant) return '';
    if (activePlayerPropVariant.lines.length === 0) return '';
    if (activePlayerPropVariant.lines.includes(activePlayerPropLine)) return activePlayerPropLine;
    return getPreferredPlayerPropLine(activePlayerPropVariant.lines);
  }, [activePlayerPropVariant, activePlayerPropLine]);

  React.useEffect(() => {
    if (!activePlayerPropGroup) {
      if (activePlayerPropGroupId !== '') setActivePlayerPropGroupId('');
      if (activePlayerPropVariantId !== '') setActivePlayerPropVariantId('');
      if (activePlayerPropLine !== '') setActivePlayerPropLine('');
      return;
    }

    if (activePlayerPropGroupId !== activePlayerPropGroup.id) {
      setActivePlayerPropGroupId(activePlayerPropGroup.id);
      return;
    }

    if (activePlayerPropVariant && activePlayerPropVariantId !== activePlayerPropVariant.id) {
      setActivePlayerPropVariantId(activePlayerPropVariant.id);
      return;
    }

    if (activePlayerPropResolvedLine !== activePlayerPropLine) {
      setActivePlayerPropLine(activePlayerPropResolvedLine);
    }
  }, [
    activePlayerPropGroup,
    activePlayerPropVariant,
    activePlayerPropResolvedLine,
    activePlayerPropGroupId,
    activePlayerPropVariantId,
    activePlayerPropLine,
  ]);

  const sortIndex = React.useMemo(() => buildSortIndex(sortBy), [sortBy]);

  const allowedColumns = React.useMemo(() => {
    // merge columns from active tab and sub-tab (sub-tab takes precedence if defined)
    const cols = currentSubTab?.columns ?? currentTab?.columns;
    if (!cols || cols.length === 0) return undefined;

    const normalizedColumns = cols.map((c) => c.toLowerCase());
    const configuredColumns = new Set(normalizedColumns);
    const usedColumns = new Set<string>();

    for (const market of filteredMarkets) {
      for (const outcome of market.outcomes ?? []) {
        const columnKey = getOutcomeColumnKey(outcome);
        if (configuredColumns.has(columnKey)) {
          usedColumns.add(columnKey);
        }
      }
    }

    return new Set(normalizedColumns.filter((columnKey) => usedColumns.has(columnKey)));
  }, [currentTab, currentSubTab, filteredMarkets]);

  const sections: MarketSection[] = React.useMemo(() => {
    const raw = filteredMarkets.map((m: any) =>
      buildMarketSection(m, lang, sortIndex, allowedColumns, correctScoreColumnLabels),
    );
    const nonEmpty = raw.filter((s) => s.header.length > 0);
    return mergeFlatSections(nonEmpty, lang);
  }, [filteredMarkets, lang, sortIndex, allowedColumns, correctScoreColumnLabels]);

  // collect all unique non-label columns across all sections for column pagination
  const columnOptions = React.useMemo(() => {
    const seen = new Set<string>();
    const options: HeaderCell[] = [];
    for (const section of sections) {
      for (const h of section.header) {
        if (h.value !== '_label' && h.value !== '_market' && !seen.has(h.value)) {
          seen.add(h.value);
          options.push(h);
        }
      }
    }
    return options;
  }, [sections]);

  const estimatedLabelColumnWidth = React.useMemo(() => {
    const labelSamples: string[] = [];

    for (const section of sections) {
      const hasLabelColumn = section.header[0]?.value === '_label';
      const headerLabel = hasLabelColumn ? resolveLabel(section.header[0]?.label ?? '', lang) : '';
      if (hasLabelColumn && headerLabel) {
        labelSamples.push(headerLabel);
      }

      if (!hasLabelColumn) {
        continue;
      }

      for (const row of section.rows) {
        const firstCell = row.cells[0];
        if (firstCell?.type === 'label' && firstCell.value) {
          labelSamples.push(firstCell.value);
        }
      }
    }

    if (labelSamples.length === 0) {
      return 0;
    }

    const longestLabel = labelSamples.reduce(
      (longest, current) => (current.length > longest.length ? current : longest),
      '',
    );

    return estimateTextWidth(
      longestLabel,
      MIN_TABLE_LABEL_COLUMN_WIDTH,
      MAX_TABLE_LABEL_COLUMN_WIDTH,
      TABLE_LABEL_CHAR_WIDTH,
    );
  }, [sections, lang]);

  const estimatedColumnWidths = React.useMemo(() => {
    return columnOptions.map((column) =>
      estimateTextWidth(
        resolveLabel(column.label, lang),
        MIN_TABLE_ODD_COLUMN_WIDTH,
        MAX_TABLE_ODD_COLUMN_WIDTH,
        TABLE_ODD_HEADER_CHAR_WIDTH,
      ),
    );
  }, [columnOptions, lang]);

  const totalColumnCount = columnOptions.length;
  const forcedColumnViewportSize = currentSubTab?.forcedColumnViewportSize ?? currentTab?.forcedColumnViewportSize;
  const defaultColumnViewportSize = getDefaultColumnViewportSize(
    moduleWidth,
    estimatedLabelColumnWidth,
    estimatedColumnWidths,
    currentSubTab?.preferredColumnViewportSize ?? currentTab?.preferredColumnViewportSize,
  );
  const effectiveColumnViewportSize =
    totalColumnCount === 0
      ? 0
      : forcedColumnViewportSize != null && forcedColumnViewportSize > 0
        ? Math.min(forcedColumnViewportSize, totalColumnCount)
        : columnViewportSize == null || columnViewportSize <= 0
          ? defaultColumnViewportSize
          : Math.min(columnViewportSize, totalColumnCount);
  const maxColumnViewportStart = Math.max(0, totalColumnCount - effectiveColumnViewportSize);
  const clampedColumnViewportStart = Math.min(columnViewportStart, maxColumnViewportStart);
  const columnViewportEnd =
    effectiveColumnViewportSize === 0 ? 0 : clampedColumnViewportStart + effectiveColumnViewportSize - 1;
  const visibleColumnOptions = columnOptions.slice(
    clampedColumnViewportStart,
    clampedColumnViewportStart + effectiveColumnViewportSize,
  );
  const visibleSections = React.useMemo(
    () => buildVisibleSections(sections, visibleColumnOptions),
    [sections, visibleColumnOptions],
  );
  const hasPrevColumnPage = clampedColumnViewportStart > 0;
  const hasNextColumnPage = clampedColumnViewportStart < maxColumnViewportStart;
  const hiddenLeftCount = clampedColumnViewportStart;
  const hiddenRightCount = Math.max(0, totalColumnCount - (columnViewportEnd + 1));
  const isColumnViewported = effectiveColumnViewportSize > 0 && effectiveColumnViewportSize < totalColumnCount;
  const estimatedFullTableWidth = getEstimatedFullTableWidth(estimatedLabelColumnWidth, estimatedColumnWidths);
  const shouldUseColumnViewport =
    forcedColumnViewportSize != null && forcedColumnViewportSize >= totalColumnCount
      ? false
      : totalColumnCount > 0 && moduleWidth > 0 && estimatedFullTableWidth > moduleWidth;
  const canShowAllColumns = !shouldUseColumnViewport;

  React.useEffect(() => {
    if (columnViewportStart !== clampedColumnViewportStart) {
      setColumnViewportStart(clampedColumnViewportStart);
    }
  }, [columnViewportStart, clampedColumnViewportStart]);

  const onColumnViewportPrev = React.useCallback(() => {
    setColumnViewportStart((prev) => Math.max(0, prev - Math.max(1, effectiveColumnViewportSize)));
  }, [effectiveColumnViewportSize]);

  const onColumnViewportNext = React.useCallback(() => {
    setColumnViewportStart((prev) => prev + Math.max(1, effectiveColumnViewportSize));
  }, [effectiveColumnViewportSize]);

  const onColumnViewportSetStart = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const start = Number(e.currentTarget.dataset.columnViewportStart ?? e.currentTarget.dataset.columnStart);
    if (isNaN(start)) return;
    setColumnViewportStart(Math.max(0, start));
    e.stopPropagation();
  }, []);

  const onColumnViewportSetSize = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const size = Number(e.currentTarget.dataset.columnViewportSize ?? e.currentTarget.dataset.columnWindowSize);
    if (isNaN(size)) return;
    setColumnViewportSize(size > 0 ? size : null);
    setColumnViewportStart(0);
    e.stopPropagation();
  }, []);

  const onColumnViewportReset = React.useCallback((e?: React.MouseEvent<HTMLElement>) => {
    setColumnViewportStart(0);
    setColumnViewportSize(null);
    e?.stopPropagation();
  }, []);

  const onPlayerPropGroupClick = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    try {
      e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    } catch (e) {
      /** */
    }

    const id = e.currentTarget.dataset.playerPropGroup ?? '';
    setActivePlayerPropGroupId(id);
    setActivePlayerPropVariantId('');
    setActivePlayerPropLine('');
  }, []);

  const onPlayerPropVariantClick = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const id = e.currentTarget.dataset.playerPropVariant ?? '';
    setActivePlayerPropVariantId(id);
    setActivePlayerPropLine('');
  }, []);

  const onPlayerPropLineClick = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const value = e.currentTarget.dataset.playerPropLine ?? '';
    setActivePlayerPropLine(value);
  }, []);

  const playerPropGroups = React.useMemo<PlayerPropGroupOption[]>(
    () =>
      playerPropGroupsData.map((group, index) => ({
        id: group.id,
        label: group.label,
        active: group.id === activePlayerPropGroup?.id,
        index,
        playerCount: group.playerCount,
        variantCount: group.variants.length,
      })),
    [playerPropGroupsData, activePlayerPropGroup],
  );

  const playerPropVariants = React.useMemo<PlayerPropVariantOption[]>(
    () =>
      (activePlayerPropGroup?.variants ?? []).map((variant, index) => ({
        id: variant.id,
        label: variant.label,
        active: variant.id === activePlayerPropVariant?.id,
        index,
      })),
    [activePlayerPropGroup, activePlayerPropVariant],
  );

  const playerPropLines = React.useMemo<PlayerPropLineOption[]>(
    () =>
      (activePlayerPropVariant?.lines ?? [])
        .filter((line) => line !== '__single')
        .map((line, index) => ({
          value: line,
          label: line,
          active: line === activePlayerPropResolvedLine,
          index,
        })),
    [activePlayerPropVariant, activePlayerPropResolvedLine],
  );

  const playerPropRows = React.useMemo<PlayerPropRow[]>(() => {
    if (!activePlayerPropVariant) return [];

    const lineKey = activePlayerPropResolvedLine || '__single';

    return activePlayerPropVariant.players
      .map((player) => ({
        playerName: player.playerName,
        outcome: player.outcomesByLine[lineKey] ?? null,
        hasOutcome: player.outcomesByLine[lineKey] != null,
      }))
      .filter((row) => row.hasOutcome)
      .sort((a, b) => {
        if (a.hasOutcome && b.hasOutcome) {
          const aValue = Number(a.outcome?.oddValue ?? Number.POSITIVE_INFINITY);
          const bValue = Number(b.outcome?.oddValue ?? Number.POSITIVE_INFINITY);
          if (aValue !== bValue) return aValue - bValue;
        }

        if (a.hasOutcome !== b.hasOutcome) return a.hasOutcome ? -1 : 1;
        return NATURAL_SORTER.compare(a.playerName, b.playerName);
      });
  }, [activePlayerPropVariant, activePlayerPropResolvedLine]);

  const playerPropSections = React.useMemo<MarketSection[]>(() => {
    const activeLabelParts = [
      activePlayerPropGroup?.label ?? '',
      activePlayerPropVariant && activePlayerPropVariant.id !== 'base' ? activePlayerPropVariant.label : '',
      activePlayerPropResolvedLine && activePlayerPropResolvedLine !== '__single' ? activePlayerPropResolvedLine : '',
    ].filter(Boolean);
    const valueHeaderLabel = activeLabelParts.join(' ');

    if (playerPropRows.length === 0) {
      return [];
    }

    return [
      {
        market: null,
        marketName: '',
        header: [
          { label: '', value: '_label' },
          { label: valueHeaderLabel, value: 'player-prop:value' },
        ],
        rows: playerPropRows.map((row) => ({
          cells: [
            { type: 'label', value: row.playerName } as CellLabel,
            row.outcome ? ({ type: 'odd', outcome: row.outcome } as CellOdd) : ({ type: 'empty' } as CellEmpty),
          ],
        })),
      },
    ];
  }, [playerPropRows, activePlayerPropGroup, activePlayerPropVariant, activePlayerPropResolvedLine]);

  const contextValue = React.useMemo(
    () => ({
      filteredMarkets,
      sections,
      visibleSections,
      tabs: tabs.map((t, i) => ({
        id: t.id ?? '',
        label: resolveLabel(t.label, lang),
        active: i === activeTab,
        index: i,
        hasSubTabs: (t.subTabs?.length ?? 0) > 0,
      })),
      subTabs: currentSubTabs.map((st, i) => ({
        id: st.id ?? '',
        label: resolveLabel(st.label, lang),
        active: i === activeSubTab,
        index: i,
      })),
      hasPlayerPropView: playerPropGroups.length > 0,
      playerPropGroups,
      activePlayerPropGroupId: activePlayerPropGroup?.id ?? '',
      activePlayerPropGroupLabel: activePlayerPropGroup?.label ?? '',
      playerPropVariants,
      activePlayerPropVariantId: activePlayerPropVariant?.id ?? '',
      activePlayerPropVariantLabel: activePlayerPropVariant?.label ?? '',
      playerPropLines,
      activePlayerPropLine: activePlayerPropResolvedLine,
      playerPropRows,
      playerPropSections,
      activeTab,
      activeSubTab,
      forcedColumnViewportSize,
      columnViewportStart: clampedColumnViewportStart,
      defaultColumnViewportSize,
      columnViewportSize: effectiveColumnViewportSize,
      columnViewportEnd,
      totalColumnCount,
      hiddenLeftCount,
      hiddenRightCount,
      isColumnViewported,
      moduleWidth,
      estimatedFullTableWidth,
      shouldUseColumnViewport,
      canShowAllColumns,
      columnOptions: columnOptions.map((h, i) => ({
        label: resolveLabel(h.label, lang),
        value: h.value,
        active: i >= clampedColumnViewportStart && i < clampedColumnViewportStart + effectiveColumnViewportSize,
        index: i,
      })),
      visibleColumnOptions: visibleColumnOptions.map((h, i) => ({
        label: resolveLabel(h.label, lang),
        value: h.value,
        active: true,
        index: i + clampedColumnViewportStart,
      })),
      hasPrevColumnPage,
      hasNextColumnPage,
      lang,
      sortBy,
      actions: {
        onTabClick,
        onSubTabClick,
        onColumnViewportPrev,
        onColumnViewportNext,
        onColumnViewportSetStart,
        onColumnViewportSetSize,
        onColumnViewportReset,
        onPlayerPropGroupClick,
        onPlayerPropVariantClick,
        onPlayerPropLineClick,
      },
    }),
    [
      filteredMarkets,
      sections,
      visibleSections,
      tabs,
      currentSubTabs,
      playerPropGroups,
      activePlayerPropGroup,
      playerPropVariants,
      activePlayerPropVariant,
      playerPropLines,
      activePlayerPropResolvedLine,
      playerPropRows,
      playerPropSections,
      activeTab,
      activeSubTab,
      forcedColumnViewportSize,
      clampedColumnViewportStart,
      effectiveColumnViewportSize,
      defaultColumnViewportSize,
      columnViewportEnd,
      totalColumnCount,
      hiddenLeftCount,
      hiddenRightCount,
      isColumnViewported,
      moduleWidth,
      estimatedFullTableWidth,
      shouldUseColumnViewport,
      canShowAllColumns,
      columnOptions,
      visibleColumnOptions,
      hasPrevColumnPage,
      hasNextColumnPage,
      lang,
      sortBy,
      onTabClick,
      onSubTabClick,
      onColumnViewportPrev,
      onColumnViewportNext,
      onColumnViewportSetStart,
      onColumnViewportSetSize,
      onColumnViewportReset,
      onPlayerPropGroupClick,
      onPlayerPropVariantClick,
      onPlayerPropLineClick,
    ],
  );

  if (!isVisible) return null;

  return (
    <ModuleElementDiv ref={moduleRef} className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default MarketFilter;
